import { defineMessages } from "react-intl";

export const ORGANIZATION_APP_TAG = "org-app-walkthrough";
export const SIGNER_APP_TAG = "signer-app-walkthrough";

const ARROW_PLACEMENT = Object.freeze({
  logo: "topLeft",
  sidebar: "leftTop",
  widget: "topRight",
  waffle: "leftTop",
});

const STEP_NAMES = Object.freeze({
  logo: "logo",
  sidebar: "sidebar",
  widget: "widget",
  waffle: "waffle",
  orgSignerWaffle: "orgSignerWaffle",
});

export const COMMON_MESSAGES = defineMessages({
  next: {
    id: "15e1a6b8-aa77-4287-bd02-e5555f89f7d6",
    defaultMessage: "Next",
  },
  nextLabel: {
    id: "d9b7d28b-7265-43b9-a6c6-ccbf564747d0",
    defaultMessage: "Next step in walkthrough",
  },
  finish: {
    id: "690f1352-531f-4bf4-a850-49477ca8ef77",
    defaultMessage: "Finish",
  },
  finishLabel: {
    id: "32494b28-fc6d-42f6-8964-514eb0aed3b0",
    defaultMessage: "Finish walkthrough",
  },
  dismiss: {
    id: "dbe3d4fb-1e9f-4f71-9d70-6af30d1fecae",
    defaultMessage: "Dismiss",
  },
  dismissLabel: {
    id: "ebdf7d49-aa5c-4c49-84d8-3bbfde33394f",
    defaultMessage: "Dismiss walkthrough",
  },
  dontShowAgain: {
    id: "26857985-03f3-4d3d-85bf-982f0bf8ff85",
    defaultMessage: "Don't show this message again",
  },
});

const ORGANIZATION_MESSAGES = defineMessages({
  heading: {
    id: "5bf84858-96fb-46b7-b3b9-2fc080ee2bb1",
    defaultMessage: "Get to know our navigation",
  },
  sidebar: {
    id: "ad4264b4-d019-449e-9338-6845579cc42c",
    defaultMessage:
      "See your organization's name and navigate between organization features in the sidebar. Expand and collapse with the arrow.",
  },
  widget: {
    id: "ab1a7db6-ffbd-4147-824f-b11f6700eccd",
    defaultMessage:
      "Navigate to your personal Proof space from your initials. This includes your user profile and personal documents.",
  },
  widgetNotary: {
    id: "428bf87d-5a9c-4508-b439-78d338a33e7f",
    defaultMessage:
      "Navigate to your personal Proof space from your initials. This includes your user profile, notary profile, and personal documents.",
  },
});

const ORGANIZATION_LOGO_MESSAGES = defineMessages({
  title: {
    id: "3f91951a-3278-4ed9-acb0-c97f2c00bdef",
    defaultMessage:
      "This is your global navigation bar. “Proof for Title” indicates that you are in our title agent app.",
  },
  lender: {
    id: "7568e499-ebc2-4d6e-a49d-95f306ee78c2",
    defaultMessage:
      "This is your global navigation bar. “Proof for Lenders” indicates that you are in our lender app.",
  },
  business: {
    id: "c539f897-59c7-4d6f-9e14-02dce1ffedb7",
    defaultMessage:
      "This is your global navigation bar. “Proof for Business” indicates that you are in our business app.",
  },
  notary: {
    id: "236e2ea8-4bbc-4a44-93e2-0782a217dff5",
    defaultMessage:
      "This is your global navigation bar. “Proof for Notaries” indicates that you are in our notary app.",
  },
});

const ORGANIZATION_WAFFLE_MESSAGES = defineMessages({
  title: {
    id: "f2d3fecc-6757-43bc-8466-ae47cee9b1c4",
    defaultMessage:
      "Get back to Proof for Title from your personal Proof space by opening up the dot menu in the top left corner.",
  },
  lender: {
    id: "b9aad310-c031-4107-b47d-d8e8c00a9afa",
    defaultMessage:
      "Get back to Proof for Lenders from your personal Proof space by opening up the dot menu in the top left corner.",
  },
  business: {
    id: "bc0ab453-cbef-483a-9acf-77d732cbc4c6",
    defaultMessage:
      "Get back to Proof for Business from your personal Proof space by opening up the dot menu in the top left corner.",
  },
  notary: {
    id: "850c7e28-47df-419a-ab69-c0fbbff81e29",
    defaultMessage:
      "Get back to Proof for Notaries from your personal Proof space by opening up the dot menu in the top left corner.",
  },
});

export const ORGANIZATION_STEPS = [
  {
    stepName: STEP_NAMES.logo,
    heading: ORGANIZATION_MESSAGES.heading,
    description: {
      title: ORGANIZATION_LOGO_MESSAGES.title,
      lender: ORGANIZATION_LOGO_MESSAGES.lender,
      business: ORGANIZATION_LOGO_MESSAGES.business,
      notary: ORGANIZATION_LOGO_MESSAGES.notary,
    },
    buttonText: COMMON_MESSAGES.next,
    buttonLabel: COMMON_MESSAGES.nextLabel,
    arrowPlacement: ARROW_PLACEMENT.logo,
  },
  {
    stepName: STEP_NAMES.sidebar,
    heading: ORGANIZATION_MESSAGES.heading,
    description: {
      title: ORGANIZATION_MESSAGES.sidebar,
      lender: ORGANIZATION_MESSAGES.sidebar,
      business: ORGANIZATION_MESSAGES.sidebar,
      notary: ORGANIZATION_MESSAGES.sidebar,
    },
    buttonText: COMMON_MESSAGES.next,
    buttonLabel: COMMON_MESSAGES.nextLabel,
    arrowPlacement: ARROW_PLACEMENT.sidebar,
  },
  {
    stepName: STEP_NAMES.widget,
    heading: ORGANIZATION_MESSAGES.heading,
    description: {
      title: ORGANIZATION_MESSAGES.widget,
      lender: ORGANIZATION_MESSAGES.widget,
      business: ORGANIZATION_MESSAGES.widget,
      notary: ORGANIZATION_MESSAGES.widgetNotary,
    },
    buttonText: COMMON_MESSAGES.next,
    buttonLabel: COMMON_MESSAGES.nextLabel,
    arrowPlacement: ARROW_PLACEMENT.widget,
  },
  {
    stepName: STEP_NAMES.waffle,
    heading: ORGANIZATION_MESSAGES.heading,
    description: {
      title: ORGANIZATION_WAFFLE_MESSAGES.title,
      lender: ORGANIZATION_WAFFLE_MESSAGES.lender,
      business: ORGANIZATION_WAFFLE_MESSAGES.business,
      notary: ORGANIZATION_WAFFLE_MESSAGES.notary,
    },
    buttonText: COMMON_MESSAGES.finish,
    buttonLabel: COMMON_MESSAGES.finishLabel,
    arrowPlacement: ARROW_PLACEMENT.waffle,
  },
];

const ORGANIZATION_SIGNER_MESSAGES = defineMessages({
  heading: {
    id: "df32424a-d4e1-4b01-aaf1-785a9b1e9590",
    defaultMessage: "Looking for your business space?",
  },
  title: {
    id: "9d515e6d-51d7-4d3f-8123-13af37741396",
    defaultMessage:
      "You are in your personal Proof space. Get to Proof for Title by clicking this menu.",
  },
  lender: {
    id: "b5d5b048-c1cf-4c72-9b73-34cdd5a317bd",
    defaultMessage:
      "You are in your personal Proof space. Get to Proof for Lenders by clicking this menu.",
  },
  business: {
    id: "42e105fc-8a43-4edd-9db3-5a33a28e04f9",
    defaultMessage:
      "You are in your personal Proof space. Get to Proof for Business by clicking this menu.",
  },
  notary: {
    id: "9ec898a8-9b48-415d-9d85-0f0b265f6f23",
    defaultMessage:
      "You are in your personal Proof space. Get to Proof for Notaries by clicking this menu.",
  },
});
// Org user in the signer app looks different than the rest so special stepName and mismatched arrow placement name
export const ORGANIZATION_SIGNER_STEPS = [
  {
    stepName: STEP_NAMES.orgSignerWaffle,
    heading: ORGANIZATION_SIGNER_MESSAGES.heading,
    description: {
      title: ORGANIZATION_SIGNER_MESSAGES.title,
      lender: ORGANIZATION_SIGNER_MESSAGES.lender,
      business: ORGANIZATION_SIGNER_MESSAGES.business,
      notary: ORGANIZATION_SIGNER_MESSAGES.notary,
    },
    buttonText: COMMON_MESSAGES.dismiss,
    buttonLabel: COMMON_MESSAGES.dismissLabel,
    arrowPlacement: ARROW_PLACEMENT.logo,
  },
];
